// eslint-disable-next-line
import React, { useState } from 'react';
import './ResetPassword.css';
import { Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginImage from '../../../assets/LoginIcon/login-image.png';
import LogoWithName from '../../../assets/LoginIcon/logo-with-name-icon.svg';
import ResetPasswordImage from '../../../assets/LoginIcon/reset-password-icon.svg';
import ResetPasswordSuccessImage from '../../../assets/LoginIcon/reset-pass-success-icon.svg';
import PasswordIcon from '../../../assets/LoginIcon/password-icon.svg';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/LanguageContext';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMesssage] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const token = query.get('token');
  const { translate } = useTranslation();

  const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

  const handleUserPasswordChange = e => {
    setPassword(e.target.value);
    setPasswordError(null);
  };
  const handleUserConfirmPasswordChange = e => {
    setConfirmPassword(e.target.value);
    setPasswordError(null);
  };
  const handleClickShowPassword = () => {
    setShowPassword(prev => !prev);
  };
  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(prev => !prev);
  };

  const password_validate = password => {
    const re = {
      capital: /(?=.*[A-Z])/,
      length: /.{8,40}/,
      specialChar: /[ -/:-@[-`{-~]/,
      digit: /(?=.*[0-9])/,
    };

    if (!re.length.test(password)) {
      return `${translate('resetPassword', 'passwordLength')}`;
    }
    if (!re.capital.test(password)) {
      return `${translate('resetPassword', 'passwordUppercase')}`;
    }
    if (!re.specialChar.test(password)) {
      return `${translate('resetPassword', 'passwordSpecial')}`;
    }
    if (!re.digit.test(password)) {
      return `${translate('resetPassword', 'passwordDigit')}`;
    }

    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const passwordValidationError = password_validate(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      toast.error(`${translate('resetPassword', 'passwordsNotMatch')}`);
      return;
    }

    try {
      const newData = {
        token: token,
        password: password,
      };

      const res = await axios.post(`${SERVER_API_URL}/auth/reset-password`, newData);
      if (res?.status === 200) {
        setSuccessMesssage(true);
        setPasswordError(null);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box className="reset-password-container">
      <Grid container spacing={0} className="reset-password-grid-container">
        <Grid className="reset-password-grid-left">
          <Box className="reset-password-left-container">
            <img src={LogoWithName} alt="logo-with-name" className="logo-with-name" />
            {successMessage === false ? (
              <Box className=" flex flex-col justify-start items-start gap-5 mt-[50px]">
                <img src={ResetPasswordImage} alt="reset-password-image" className="pb-2" />
                <Box className="flex flex-col justify-start items-start gap-0.5">
                  <Typography variant="h4" className="reset-password-welcome-back-text">
                    {translate('resetPassword', 'confirmPasswordText')}
                  </Typography>
                  <Typography variant="body1" className="reset-password-to-your-account-text">
                    {translate('resetPassword', 'enterNewPasswordText')}
                  </Typography>
                </Box>
                <form
                  className="flex flex-col justify-start items-start gap-2 w-[85%]"
                  onSubmit={e => handleSubmit(e)}
                >
                  <Box className="flex flex-col gap-2 w-full">
                    <label htmlFor="password" className="reset-password-email-label">
                      {translate('resetPassword', 'passwordInputText')}
                    </label>
                    <Box className="reset-password-email-input-box">
                      <img
                        src={PasswordIcon}
                        alt="password-icon"
                        className="reset-password-email-icon"
                      />
                      <input
                        className="reset-password-email-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={translate('resetPassword', 'passwordInputPlaceholder')}
                        value={password}
                        onChange={handleUserPasswordChange}
                        required
                      />
                      <div
                        onClick={handleClickShowPassword}
                        className="password-toggle-button"
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? (
                          <VisibilityOff className="password-visibility-icon" />
                        ) : (
                          <Visibility className="password-visibility-icon" />
                        )}
                      </div>
                    </Box>
                    {passwordError && (
                      <div className="validation-error-container">
                        <div className="validation-error-icon">!</div>
                        {passwordError}
                      </div>
                    )}
                  </Box>
                  <Box className="flex flex-col gap-2 w-full">
                    <label htmlFor="password" className="reset-password-email-label">
                      {translate('resetPassword', 'confirmPasswordInputText')}
                    </label>
                    <Box className="reset-password-email-input-box">
                      <img
                        src={PasswordIcon}
                        alt="password-icon"
                        className="reset-password-email-icon"
                      />
                      <input
                        className="reset-password-email-input"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={translate('resetPassword', 'confirmPasswordInputPlaceholder')}
                        value={confirmPassword}
                        onChange={handleUserConfirmPasswordChange}
                        required
                      />
                      <div
                        onClick={handleClickShowConfirmPassword}
                        className="password-toggle-button"
                        aria-label="toggle password visibility"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff className="password-visibility-icon" />
                        ) : (
                          <Visibility className="password-visibility-icon" />
                        )}
                      </div>
                    </Box>
                    {passwordError && (
                      <div className="validation-error-container">
                        <div className="validation-error-icon">!</div>
                        {passwordError}
                      </div>
                    )}
                  </Box>
                  <button
                    type="submit"
                    variant="contained"
                    className="reset-password-button"
                    data-testid="submit"
                  >
                    {translate('resetPassword', 'confirmPasswordButtonText')}
                  </button>
                </form>
              </Box>
            ) : (
              <Box className="flex flex-col justify-start items-start gap-5 mt-[100px] w-[80%]">
                <Box className="reset-password-success-image-container">
                  <img
                    src={ResetPasswordSuccessImage}
                    alt="reset-password-success-image"
                    className="pb-2"
                  />
                </Box>
                <Typography variant="h4" className="reset-password-welcome-back-text">
                  {translate('resetPassword', 'congratulationText')}
                </Typography>
                <Typography variant="body1" className="reset-password-to-your-account-text">
                  {translate('resetPassword', 'congratulationMessageText')}
                </Typography>
                <button
                  onClick={() => navigate('/')}
                  variant="contained"
                  className="reset-password-button"
                >
                  {translate('resetPassword', 'signInButton')}
                </button>
              </Box>
            )}
          </Box>
          {/* <Box className="bottom-div">
            <Typography variant="body2" className="privacy-policy-text">
              {' '}
              <Link to="/privacy-policy">{translate('login', 'privacyPolicyText')}</Link>
            </Typography>
            <Divider orientation="vertical" flexItem style={{ color: '#949DA6' }} />
            <Typography variant="body2" className="privacy-policy-text">
              {' '}
              <Link to="/privacy-policy"> {translate('login', 'termConditionText')} </Link>
            </Typography>
          </Box> */}
        </Grid>
        <Grid className="reset-password-grid-right">
          <Box className="login-right-container">
            <Box className="reset-password-right-container-box-container">
              {/* <Typography className="reset-password-right-container-box-container-text">
                {translate('resetPassword', 'loginImageText')}
              </Typography> */}
              <img src={LoginImage} alt="login-image" />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPassword;
